import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useApi, useMacavityJobStatus } from '../../../../hooks';
import { ActivityIndicator, Button } from '../../../../ui';
import CellMap from './CellMap';
import { clusterDenom } from './compositionChartData';

// UND-656: Underwriting Authority panel. Reads the latest designation from
// GET /quotes/{id}/authority (UND-654, recompute-on-miss server-side) and renders it per gate.
// The engine runs dark today, so gates lacking inputs come back CANNOT EVALUATE (amber) and light
// up as UND-653 (Gates 1-2) and UND-677 (Gate 4) wire in — "the UI is the signal".

const C = {
  ink900: '#212B36',
  ink700: '#3e4149',
  ink500: '#637381',
  ink300: '#919EAB',
  border: '#DFE3E8',
  green: '#1CB24F',
  greenBg: '#e9f8ee',
  greenText: '#166534',
  amberText: '#8a5a00',
  amberBg: '#fdf3e0',
  amberBorder: '#f3ddb2',
  amberSolid: '#d99a23',
  redText: '#991b1b',
  redBg: '#fdeaea',
  error: '#DF4661',
  headerBg: '#F9FAFB',
  navy: '#436CBA',
  teal: '#078880',
  primary: '#5F2994',
};

const DESIGNATION = {
  WITHIN_AUTHORITY: {
    label: 'Pass',
    badge: C.green,
    bg: C.greenBg,
    border: '#cde9d6',
    msg: 'All gates clear — this quote can auto-bind without a carrier referral.',
  },
  REFERRAL_REQUIRED: {
    label: 'Referral Required',
    badge: C.amberSolid,
    bg: C.amberBg,
    border: C.amberBorder,
    msg: 'This quote cannot auto-bind — carrier referral to Skyward is required.',
  },
  // Retired 2026-07-01 — "Outside Authority" folded into Referral Required (a hard fail still just
  // routes to a human review). Kept defensively in case an older persisted snapshot carries it.
  OUTSIDE_AUTHORITY: {
    label: 'Referral Required',
    badge: C.amberSolid,
    bg: C.amberBg,
    border: C.amberBorder,
    msg: 'This quote cannot auto-bind — carrier referral to Skyward is required.',
  },
  // Frontend overlay while an async macavity run is in flight — the outcome isn't decided yet.
  PENDING: {
    label: 'Pending',
    badge: C.ink500,
    bg: C.headerBg,
    border: C.border,
    msg: 'Catastrophe terms are still computing — authority resolves when the macavity run completes.',
  },
};

// Two-state model (2026-07-01): non-diagnostic gates show only "Pass" or "Referral Required". Every
// blocking outcome (FAIL/REVIEW/REFERRAL/ERROR) collapses to "Referral Required"; WARN is reserved for
// diagnostic gates; N/A is a muted non-applicable label; PENDING is a frontend-only transient shown
// while an async macavity run is in flight.
const OUTCOME = {
  PASS: { label: 'Pass', color: C.greenText, bg: C.greenBg, icon: '✓', iconColor: C.green },
  FAIL: { label: 'Referral Required', color: C.amberText, bg: C.amberBg, icon: '⚠', iconColor: C.amberSolid },
  REVIEW: { label: 'Referral Required', color: C.amberText, bg: C.amberBg, icon: '⚠', iconColor: C.amberSolid },
  REFERRAL: { label: 'Referral Required', color: C.amberText, bg: C.amberBg, icon: '⚠', iconColor: C.amberSolid },
  ERROR: { label: 'Referral Required', color: C.amberText, bg: C.amberBg, icon: '⚠', iconColor: C.amberSolid },
  // Non-blocking advisory (diagnostic gates only, e.g. Gate 6 data-quality) — info icon, never refers.
  WARN: { label: 'Warn', color: C.amberText, bg: C.amberBg, icon: 'ⓘ', iconColor: C.amberSolid },
  NOT_APPLICABLE: { label: 'N/A', color: C.ink500, bg: '#eef1f3', icon: '–', iconColor: C.ink300 },
  // Frontend-only: a macavity-gated check awaiting an in-flight run.
  PENDING: { label: 'Pending', color: C.ink700, bg: C.headerBg, icon: '⏳', iconColor: C.ink500 },
};

// Per-location check status (detail.locationChecks[].status) → icon + colour. Mirrors OUTCOME but
// for the row-level check shown in the expanded "why" table.
const CHECK_STATUS = {
  PASS: { icon: '✓', color: C.green },
  FAIL: { icon: '✕', color: C.error },
  REVIEW: { icon: '⚠', color: C.amberSolid },
  WARN: { icon: 'ⓘ', color: C.amberSolid },
  NA: { icon: '–', color: C.ink300 },
  CANNOT_EVALUATE: { icon: '⚠', color: C.amberSolid },
};

// Canonical gate numbers. The engine emits its evaluators in execution order, which is NOT the
// display numbering: Gate 2b (loss-pick) and the non-blocking gwp_floor diagnostic sit between
// numbered gates, and Gate 9 (TIV concentration) was retired into Gate 5. So number by gate key,
// not array index. Unknown keys fall back to positional numbering.
const GATE_NUMBER = {
  gwp_bounds: '1',
  technical_premium: '2',
  loss_pick_max: '2b',
  gwp_floor: '–', // non-blocking diagnostic — not a numbered gate
  auto_fac_spa: '3',
  tiv_aggregation: '4',
  tiv_ring_aggregation: '4',
  flood_concentration: '5',
  data_quality: '6',
  flood_sublimit_compliance: '7',
  wh_referral: '8',
  wildfire: '9',
  fp_vehicle_limits: '10',
};

// Non-blocking gates shown with a "Diagnostic" tag — advisory only, never change the designation.
const DIAGNOSTIC_TAGGED = new Set(['data_quality']);

// Gates whose inputs come from the macavity recompute: Gate 2b reads the cap_5m loss run, the Gate 4
// hex-cell PML check reads the PML cell registry. The Gate 4 TIV rings check reads nothing macavity
// computes, so it is not one of them. While a recompute is in flight these read CANNOT EVALUATE, so we tag
// them "recomputing…" rather than leaving them looking like a permanent referral.
const MACAVITY_GATES = new Set(['loss_pick_max', 'tiv_aggregation']);

const ACRONYMS = new Set(['gwp', 'tiv', 'aop', 'scs', 'spa', 'wh', 'mce', 'ir']);

// Display labels that don't follow the title-cased-key default.
const GATE_LABEL = {
  loss_pick_max: 'Loss Pick Ceiling',
  auto_fac_spa: 'Auto Fac / SPA',
  tiv_aggregation: 'Catastrophe build-up: hex-cell PML',
  tiv_ring_aggregation: 'Catastrophe build-up: TIV rings',
  flood_sublimit_compliance: 'Flood Sublimit',
  wh_referral: 'W/H Deductible',
  wildfire: 'Wildfire Exclusion',
  fp_vehicle_limits: 'FP / Vehicle Limits',
};

// One-line threshold / check criteria shown under each gate name. Profile-invariant values are
// exact; auto_fac_spa / flood_concentration reflect the default (CURRENT) authority profile and
// would read differently under another profile.
const GATE_CRITERIA = {
  gwp_bounds: '$100k ≤ GWP < $750k',
  technical_premium: 'GWP ≥ 80% of technical premium',
  loss_pick_max: '≤ 60% (advisory decline > 80%)',
  auto_fac_spa: 'per-loc TIV > $50M, NW Tier-1 TIV > $5M, or peril sublimit > $5M',
  tiv_aggregation: '1-in-250 PML ≤ $25 / 30 / 35 / 40M at 5 / 10 / 25 / 50 mi',
  tiv_ring_aggregation: 'Average TIV ≤ Table 1 limit for the worst SCS zone at 5 / 10 / 25 / 50 mi',
  flood_concentration: 'QS-capped flood ≥ $25M within 5 mi (location count is advisory only)',
  data_quality: 'advisory — flood-exposed locations missing KatRisk data',
  flood_sublimit_compliance: 'flood limit ≤ KatRisk-tier maximum',
  wh_referral: 'hail deductible above the SCS-zone minimum',
  wildfire: 'HazardHub wildfire score C/D/F (renewal: only above $25M max TIV)',
  fp_vehicle_limits: 'per auto > $250K, FP per loc/occ > $100K, or FP aggregate > $200K (absent = not applicable)',
};

function gateName(key) {
  if (!key) return 'Gate';
  if (GATE_LABEL[key]) return GATE_LABEL[key];
  return key
    .split('_')
    .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// The TIV rings check's portfolio sites in range that it could not fully measure, as one line;
// null when there are none.
function portfolioGapNote(detail) {
  const withoutZone = Number(detail && detail.portfolioSitesWithoutZone) || 0;
  const withoutTiv = Number(detail && detail.portfolioSitesWithoutTiv) || 0;
  const parts = [];
  if (withoutZone > 0) parts.push(`${withoutZone} without an SCS zone`);
  if (withoutTiv > 0) parts.push(`${withoutTiv} without TIV`);
  return parts.length ? `Portfolio sites in range: ${parts.join(', ')}` : null;
}

// A finding's display text. A cannot-evaluate reason gets the "cannot evaluate — " prefix unless the
// gate already wrote it (the TIV rings check and the hex-cell PML check do).
function findingText(f) {
  const reason = withCommas(f.reason);
  if (!f.cannotEvaluate || String(f.reason || '').startsWith('cannot evaluate')) return reason;
  return `cannot evaluate — ${reason}`;
}

function fmtM(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n)}`;
}

function fmtUsd(v) {
  return `$${Math.round(Number(v) || 0).toLocaleString('en-US')}`;
}

// Add US thousands separators to whole-dollar amounts ($10000 -> $10,000) embedded in a
// backend-generated string (gate summaries / finding reasons). Leaves abbreviated values
// ($84.5M) and sub-$1,000 amounts untouched.
function withCommas(text) {
  if (text == null) return text;
  return String(text).replace(/\$(\d{4,})/g, (_, n) => `$${Number(n).toLocaleString('en-US')}`);
}

// One cluster's stacked bars (in-force / this-quote amounts vs each radius's limit) + legend. A TIV
// rings cluster labels each radius with its own ring's zone; a hex-cell PML cluster's limit is the
// flat PML cap.
const ClusterBody = ({ centerRows, rings }) => {
  // UND-805 #3: one shared scale across the cluster's radius bars, so equal dollars render at equal
  // width and the limit ticks step rightward (25 → 30 → 35 → 40M) instead of each bar
  // normalizing to its own limit (which made a fixed dollar amount look like a different fraction).
  const denom = clusterDenom(centerRows);
  const pct = (x) => `${(x / denom) * 100}%`;
  return (
    <>
      {centerRows.map((r) => {
        const inForce = Number(r.inForce) || 0;
        const quote = Number(r.quote) || 0;
        const total = Number(r.total) || inForce + quote;
        const limit = Number(r.limit) || 0;
        return (
          <div key={r.radiusMi} style={S.aggRow}>
            <span style={S.aggRadius}>{rings ? `${r.radiusMi} mi · zone ${r.zone}` : `${r.radiusMi} mi`}</span>
            <div style={S.barwrap}>
              <div style={S.bar}>
                <span style={{ ...S.seg, width: pct(inForce), background: C.navy }} />
                <span style={{ ...S.seg, width: pct(quote), background: C.primary }} />
              </div>
              <span style={{ ...S.limitMark, left: pct(limit) }} />
            </div>
            <span style={{ ...S.aggTotal, color: r.breached ? C.error : C.ink500 }}>
              <b style={{ color: r.breached ? C.error : C.ink900 }}>{fmtM(total)}</b> / {fmtM(limit)}
              {r.breached ? ` ▲ +${fmtM(total - limit)}` : ''}
            </span>
          </div>
        );
      })}
      <div style={S.legend}>
        <span>
          <i style={{ ...S.legSwatch, background: C.navy }} /> In-force
        </span>
        <span>
          <i style={{ ...S.legSwatch, background: C.primary }} /> This quote
        </span>
        <span>
          <i style={S.legLimit} /> {rings ? 'Table 1 limit' : 'PML cap'}
        </span>
      </div>
    </>
  );
};

// Gate-4 cluster composition: stacked in-force / this-quote amounts vs the PML cap (hex-cell PML
// check) or the Table 1 limit (TIV rings check), per (center location × radius). Reads gate.detail.compositionRows (engine emits it — UND-666 B8), so
// no backend change. The renewal segment is hidden until UND-653's renewal overlay populates it
// (the field stays 0 on the wire — UND-805). With more than one cluster, each becomes its own
// collapsed subsection.
// UND-707/715: how a cluster's accumulation figure was derived, so the underwriter reads it right.
//   PROJECTED  — the roster ∪ quote joint sim: THIS QUOTE IS COUNTED (the preferred basis).
//   COMBINED   — book-only: the quote is NOT yet counted (projection missing or a later bind made it
//                stale) — re-run authority checks to include it.
//   STANDALONE — greenfield without a fresh projection: the location's own 1-in-250 (UND-707).
const BASIS_BADGE = {
  PROJECTED: {
    text: '· projected — includes this quote',
    color: C.greenText,
    title:
      'The accumulation is the joint simulation of the bound book PLUS this quote’s locations — "the cell as it would be if this quote joined it."',
  },
  COMBINED: {
    text: '· book only — this quote not yet counted',
    color: C.amberText,
    title:
      'The accumulation is the bound book’s figure only; this quote’s locations are not included yet (the projection is missing or a newer bind superseded it). Use "Re-run authority checks" to include them.',
  },
  STANDALONE: {
    text: '· standalone basis (first risk in cell)',
    color: C.ink500,
    title:
      'No bound policies in this hex cell yet. Gate 4 evaluates this location on its own 1-in-250 PML — for a lone risk that equals the combined figure. It switches to the projected/combined book once the cell is occupied.',
  },
};

const BasisBadge = ({ basis }) => {
  const b = BASIS_BADGE[basis];
  if (!b) return null;
  return (
    <span title={b.title} style={{ color: b.color, fontWeight: 500, fontSize: 11, marginLeft: 6 }}>
      {b.text}
    </span>
  );
};

// A cluster's display basis: uniform basis wins; any book-only row makes the whole cluster read
// "not yet counted" (the conservative label — one un-projected radius means the quote isn't fully in).
function clusterBasis(centerRows) {
  if (!centerRows.length) return null;
  if (centerRows.some((r) => !r.basis || r.basis === 'COMBINED')) return 'COMBINED';
  if (centerRows.every((r) => r.basis === 'PROJECTED')) return 'PROJECTED';
  if (centerRows.every((r) => r.basis === 'STANDALONE')) return 'STANDALONE';
  return 'PROJECTED';
}

// `rings` draws the TIV rings check: each ring has its own worst zone, and its rows carry no PML
// basis, so the title has no zone and there is no basis badge.
const CompositionChart = ({ rows, locName = (x) => x, rings = false }) => {
  const byCenter = {};
  rows.forEach((r) => {
    (byCenter[r.centerLocationId] = byCenter[r.centerLocationId] || []).push(r);
  });
  const centers = Object.keys(byCenter);
  const multi = centers.length > 1;
  return (
    <div>
      {centers.map((center) => {
        const centerRows = byCenter[center].slice().sort((a, b) => a.radiusMi - b.radiusMi);
        const zone = centerRows.length ? centerRows[0].zone : '';
        const breached = centerRows.some((r) => r.breached);
        const basis = rings ? null : clusterBasis(centerRows);
        const title = rings ? `Cluster — ${locName(center)}` : `Cluster — ${locName(center)} · SCS zone ${zone}`;
        if (multi) {
          return (
            <details key={center} style={S.aggDetails}>
              <summary style={S.aggSummary}>
                {title}
                <BasisBadge basis={basis} />
                {breached ? <span style={{ color: C.error, fontWeight: 600 }}> · ▲ over limit</span> : null}
              </summary>
              <div style={S.aggBlock}>
                <ClusterBody centerRows={centerRows} rings={rings} />
              </div>
            </details>
          );
        }
        return (
          <div key={center} style={S.aggBlock}>
            <h4 style={S.h4}>
              {title}
              <BasisBadge basis={basis} />
            </h4>
            <ClusterBody centerRows={centerRows} rings={rings} />
          </div>
        );
      })}
    </div>
  );
};

// Gate 2 build-up: a single horizontal stacked bar of the Technical Premium components
// (SCS + flood minimums + AOP + fac, plus Fac XOL for treaty-year 2026-27+ quotes), with the 0.80×TP
// authority floor marked and a GWP diamond. GWP >= the floor => PASS; below => referral. Reads
// gate.detail (engine emits the components). facXol is ABSENT (not zero) on pre-renewal snapshots,
// so those render the classic 4-segment bar unchanged (UND-723).
const TP_SEGS = [
  { key: 'minScsGwp', label: 'SCS floor', color: C.primary },
  { key: 'minFloodGwp', label: 'Flood floor', color: C.teal },
  { key: 'aopFiveYearAvgLoss', label: 'AOP (5-yr)', color: '#b9c6e8' },
  { key: 'facultative', label: 'Fac', color: '#bda4e6' },
  { key: 'facXol', label: 'Fac XOL', color: '#e6c3a4' },
];
const TechnicalPremiumChart = ({ detail }) => {
  const n = (k) => Number(detail[k]) || 0;
  const tp = n('technicalPremium') || TP_SEGS.reduce((s, seg) => s + n(seg.key), 0);
  const floor = n('floor');
  const gwp = n('gwp');
  const ratio = Number(detail.authorityRatio) || 0.8;
  const denom = Math.max(tp, gwp, floor) * 1.08 || 1;
  const pct = (x) => `${(x / denom) * 100}%`;
  const pass = gwp >= floor;
  return (
    <div style={S.tpWrap}>
      <div style={S.tpBarWrap}>
        <div style={S.tpBar}>
          {TP_SEGS.filter((s) => n(s.key) > 0).map((s) => (
            <span key={s.key} style={{ ...S.seg, width: pct(n(s.key)), background: s.color }} title={`${s.label}: ${fmtUsd(n(s.key))}`} />
          ))}
        </div>
        <span style={{ ...S.tpFloorMark, left: pct(floor) }} title={`${ratio.toFixed(2)}×TP floor ${fmtUsd(floor)}`} />
        <span style={{ ...S.tpGwpMark, left: `calc(${pct(gwp)} - 7px)` }} title={`GWP ${fmtUsd(gwp)}`}>
          ◆
        </span>
      </div>
      <div style={S.legend}>
        {TP_SEGS.filter((s) => detail[s.key] != null).map((s) => (
          <span key={s.key}>
            <i style={{ ...S.legSwatch, background: s.color }} /> {s.label}
          </span>
        ))}
        <span>
          <i style={S.legFloor} /> {ratio.toFixed(2)}×TP floor
        </span>
        <span style={{ color: C.ink900 }}>◆ GWP</span>
      </div>
      <table style={S.findings}>
        <tbody>
          {TP_SEGS.map((s) => (
            <tr key={s.key}>
              <td style={S.td}>{s.label}</td>
              <td style={S.tdNum}>{fmtUsd(n(s.key))}</td>
            </tr>
          ))}
          <tr>
            <td style={S.tdB}>Technical Premium</td>
            <td style={S.tdNumB}>{fmtUsd(tp)}</td>
          </tr>
          <tr>
            <td style={S.td}>{ratio.toFixed(2)}×TP floor</td>
            <td style={S.tdNum}>{fmtUsd(floor)}</td>
          </tr>
          <tr>
            <td style={S.tdB}>GWP (offered)</td>
            <td style={S.tdNumB}>{fmtUsd(gwp)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ ...S.tpVerdict, color: pass ? C.greenText : C.amberText }}>
        {pass
          ? `PASS — GWP is ${(gwp / tp).toFixed(2)}× TP, ${fmtUsd(gwp - floor)} over the floor.`
          : `REFERRAL — GWP is ${fmtUsd(floor - gwp)} below the ${ratio.toFixed(2)}×TP floor.`}
      </div>
    </div>
  );
};

// The advisory GWP Floor diagnostic, rendered nested inside the GWP Bounds row. Informational only —
// it never affects the designation, so it's tucked away rather than shown as a top-level gate.
const FloorDiagnostic = ({ gate }) => {
  if (!gate) return null;
  const o = OUTCOME[gate.outcome] || OUTCOME.NOT_APPLICABLE;
  return (
    <div style={S.floorRow}>
      <span style={S.floorLabel}>
        GWP Floor<span style={S.tag}>Diagnostic</span>
      </span>
      <span style={{ ...S.ic, color: o.iconColor }}>{o.icon}</span>
      <span style={S.floorSum}>{withCommas(gate.summary)}</span>
    </div>
  );
};

// Per-location "why" table: every location the gate examined, with its status and the measured value
// vs the threshold — so a passing gate is explained, not just a green check. Emitted by the
// per-location gates (W/H, Flood Sublimit, Auto Fac/SPA, Flood Concentration, Data Quality).
const LocationChecksTable = ({ rows, locName }) => (
  // Uncapped width (unlike S.findings' 700px) so the columns use the full panel on wide screens;
  // wrapped in an x-scroller so very long content never breaks the panel layout on narrow ones.
  <div style={S.checksScroll}>
    <table style={S.checksTable}>
      <thead>
        <tr>
          <th style={S.th}>Location</th>
          <th style={{ ...S.th, width: 28 }} aria-label="status" />
          <th style={S.th}>Check</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c, j) => {
          const st = CHECK_STATUS[c.status] || CHECK_STATUS.NA;
          return (
            <tr key={c.locationId || j}>
              <td style={S.ckLoc}>{locName(c.locationId)}</td>
              <td style={{ ...S.td, color: st.color, fontWeight: 700, textAlign: 'center', width: 28 }}>{st.icon}</td>
              <td style={S.ckText}>{withCommas(c.text)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Gate 2b loss-pick breakdown: the components of the implied loss pick vs the ceiling, so the pass/
// referral verdict is auditable. Reads gate.detail (the engine emits the figures). Net premium is
// GWP less every ceded fac cost; the GWP / fac / fac XOL rows render only when the snapshot carries
// them (older snapshots emit netPremium alone), and the fac XOL row only on a quote that has the
// layer (TY 2026-27+), matching the workbook Summary tab's Gross − Fac = Net arithmetic.
export const LossPickBreakdown = ({ detail }) => {
  const lossPick = Number(detail.lossPickPct) || 0;
  const maxPct = Number(detail.maxPct) || 0;
  const declinePct = Number(detail.declinePct) || 0;
  const pass = lossPick <= maxPct;
  const considerDecline = !!detail.considerDecline;
  const hasPremiumBuildUp = detail.gwp != null && detail.fac != null;
  const hasFacXol = detail.facXol != null;
  const netLabel = hasFacXol ? 'Net premium (GWP − fac − fac XOL)' : 'Net premium (GWP − fac)';
  return (
    <div style={S.tpWrap}>
      <table style={S.findings}>
        <tbody>
          <tr>
            <td style={S.td}>SCS AAL</td>
            <td style={S.tdNum}>{fmtUsd(detail.scsAal)}</td>
          </tr>
          <tr>
            <td style={S.td}>AOP (5-yr avg loss)</td>
            <td style={S.tdNum}>{fmtUsd(detail.aop)}</td>
          </tr>
          {hasPremiumBuildUp && (
            <tr>
              <td style={S.td}>GWP (offered)</td>
              <td style={S.tdNum}>{fmtUsd(detail.gwp)}</td>
            </tr>
          )}
          {hasPremiumBuildUp && (
            <tr>
              <td style={S.td}>Fac (auto / IR)</td>
              <td style={S.tdNum}>{fmtUsd(detail.fac)}</td>
            </tr>
          )}
          {hasFacXol && (
            <tr>
              <td style={S.td}>Fac XOL</td>
              <td style={S.tdNum}>{fmtUsd(detail.facXol)}</td>
            </tr>
          )}
          <tr>
            <td style={S.td}>{netLabel}</td>
            <td style={S.tdNum}>{fmtUsd(detail.netPremium)}</td>
          </tr>
          <tr>
            <td style={S.tdB}>Implied loss pick</td>
            <td style={S.tdNumB}>{lossPick}%</td>
          </tr>
          <tr>
            <td style={S.td}>Ceiling</td>
            <td style={S.tdNum}>{maxPct}%</td>
          </tr>
          <tr>
            <td style={S.td}>Advisory decline above</td>
            <td style={S.tdNum}>{declinePct}%</td>
          </tr>
        </tbody>
      </table>
      <div style={{ ...S.tpVerdict, color: pass ? C.greenText : C.amberText }}>
        {pass
          ? `PASS — implied loss pick ${lossPick}% is within the ${maxPct}% ceiling.`
          : `REFERRAL — implied loss pick ${lossPick}% exceeds the ${maxPct}% ceiling${considerDecline ? ' (consider declining)' : ''}.`}
      </div>
    </div>
  );
};

export const AuthorityPanel = ({ quote, active }) => {
  const { getQuoteAuthority, getQuoteAuthorityCells, evaluateQuoteAuthority } = useApi();
  const quoteId = quote?.id;
  const [state, setState] = useState({ loading: false, error: null, data: null });
  // Track which gate rows are expanded so the disclosure chevron can reflect open/closed state.
  const [openGates, setOpenGates] = useState({});
  const [cells, setCells] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  // Quiet re-evaluation in flight (kick=false self-heal) — drives the Pending chip like a live run.
  const [healing, setHealing] = useState(false);
  const healedFor = useRef(null);
  const requestedFor = useRef(null);

  // `silent` re-fetches without blanking the panel — used when the macavity recompute completes, so
  // the gates refresh in place rather than flashing the full-panel spinner.
  const load = useCallback(
    async (silent = false) => {
      if (!quoteId) return;
      if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await getQuoteAuthority(quoteId);
        setState({ loading: false, error: null, data });
      } catch (e) {
        requestedFor.current = null; // let a re-open retry
        // On a silent refresh keep the stale-but-rendered snapshot rather than dropping to an error screen.
        if (!silent) setState({ loading: false, error: e?.message || 'Failed to load the authority evaluation.', data: null });
      }
    },
    [quoteId, getQuoteAuthority]
  );

  // UND-715: explicit re-run (the GET above is now a pure read — no on-read self-heal). POSTs the
  // evaluate endpoint (which also kicks the async macavity -q pass when catastrophe inputs are
  // missing/stale), then silently re-fetches; if a run was kicked, the job-status poll below shows
  // the Pending overlay until it lands.
  const rerun = useCallback(async () => {
    if (!quoteId || rerunning) return;
    setRerunning(true);
    try {
      await evaluateQuoteAuthority(quoteId);
      await load(true);
    } catch (e) {
      // surfaced by useApiService's errorMessage toast; keep the current snapshot rendered.
    } finally {
      setRerunning(false);
    }
  }, [quoteId, rerunning, evaluateQuoteAuthority, load]);

  // Automatic snapshot refresh (kick=false): re-evaluates from freshly-landed macavity data WITHOUT
  // ever re-kicking a run — a permanently unevaluable location (off-grid) must not loop macavity.
  // Used when the job-status poll reports completion, and once on open when the stored snapshot says
  // Gate 4 is awaiting catastrophe results that have since arrived: the quote-creation eval races the
  // async -q pass (eval can persist ~1 min before the projections land), so the stored designation
  // can predate the model outputs it needs.
  const refreshEval = useCallback(async () => {
    if (!quoteId || healing) return;
    setHealing(true);
    try {
      await evaluateQuoteAuthority(quoteId, { kick: false });
      await load(true);
    } catch (e) {
      // keep the current snapshot rendered; the manual Re-run button remains available.
    } finally {
      setHealing(false);
    }
  }, [quoteId, healing, evaluateQuoteAuthority, load]);

  // Fetch lazily — only when the Authority tab is active (no HTTP call on page load), and only once
  // per quoteId (re-fetch when the quote changes, e.g. after an adjustment).
  useEffect(() => {
    if (active && quoteId && requestedFor.current !== quoteId) {
      requestedFor.current = quoteId;
      load();
    }
  }, [active, quoteId, load]);

  // The designation is gated on the macavity recompute. While the tab is open, watch the quote-level
  // job; when it finishes, quietly RE-EVALUATE (kick=false) so Gate 2b/4 flip from "cannot evaluate"
  // to their real outcome — GET is a pure read since UND-715, so a plain re-fetch would only re-read
  // the stale pre-run snapshot. (Same poll the CAT modeling tab uses.)
  const macavity = useMacavityJobStatus({ quoteId, enabled: !!active, onComplete: () => refreshEval() });

  // One-shot self-heal: the stored snapshot says Gate 4 is awaiting catastrophe results but no run is
  // in flight — the results likely landed after the snapshot was written (the create-time race). Re-
  // evaluate once without kicking; if the data is genuinely absent the fresh snapshot still says
  // pending and we stop — the underwriter's Re-run button is the path that starts a run.
  useEffect(() => {
    const gates = (state.data && state.data.result && state.data.result.gates) || [];
    const pending = gates.some((g) => g.detail && g.detail.catastrophePending);
    if (pending && !macavity.active && !healing && healedFor.current !== quoteId) {
      healedFor.current = quoteId;
      refreshEval();
    }
  }, [state.data, macavity.active, healing, quoteId, refreshEval]);

  if (state.error)
    return (
      <div style={S.panel}>
        <div style={{ ...S.muted, color: C.error }}>{state.error}</div>
      </div>
    );
  if (state.loading || !state.data)
    return (
      <div style={S.panel}>
        <div style={S.loading}>
          <ActivityIndicator color="$primary" />
          <div>
            <div style={S.loadingTitle}>Evaluating authority…</div>
            <div style={S.loadingSub}>Running the gate checks against this quote.</div>
          </div>
        </div>
      </div>
    );

  const data = state.data;
  const designation = data.designation || (data.result && data.result.designation);
  // Two-state top-level (Pass | Referral Required), overlaid with a transient "Pending" while an
  // async macavity run is in flight (the final outcome isn't decided until it completes).
  const d = macavity.active || healing ? DESIGNATION.PENDING : DESIGNATION[designation] || DESIGNATION.REFERRAL_REQUIRED;
  const gates = (data.result && data.result.gates) || [];
  // Resolve a location's display name from the quote (gate results carry only the GUID).
  const locById = {};
  (quote?.locations || []).forEach((l) => {
    locById[l.id] = l;
  });
  // Label a location by its NUMBER — risk_id (insurance.application_locations.risk_id), the value
  // underwriters recognize, already carried on each quote location. Append the name/address for
  // context; fall back to the name, then the raw id. Flows to every per-location gate detail
  // (composition clusters, the location-checks table, and the findings table) via this one helper.
  const locName = (id) => {
    const l = locById[id];
    const label = (l && (l.name || l.address)) || null;
    if (l && l.riskId != null) return label ? `Location ${l.riskId} — ${label}` : `Location ${l.riskId}`;
    return label || id;
  };
  // The GWP Floor diagnostic is nested under GWP Bounds, not shown as its own row.
  const gwpFloor = gates.find((g) => g.key === 'gwp_floor');
  const rows = gates.filter((g) => g.key !== 'gwp_floor');
  const evaluatedAt = data.evaluatedAt ? DateTime.fromMillis(Number(data.evaluatedAt)).toFormat('LLL d, yyyy HH:mm') : null;
  // When the referral is driven by cannot-evaluate locations (a gate passed its breach check but
  // couldn't assess some locations), name the gates so the banner isn't an unexplained referral.
  const ceGates = rows.filter((g) => (g.findings || []).some((f) => f.cannotEvaluate));
  const ceTotal = ceGates.reduce((s, g) => s + (g.findings || []).filter((f) => f.cannotEvaluate).length, 0);
  // Gate detail says results are pending (transient) vs a permanent cannot-evaluate — word the
  // referral banner accordingly so "missing data" isn't blamed on the locations when the model
  // simply hasn't run yet.
  const catPending = rows.some((g) => g.detail && g.detail.catastrophePending);
  const ceNote =
    designation === 'REFERRAL_REQUIRED' && ceTotal > 0 && !macavity.active && !healing
      ? catPending
        ? `Why: catastrophe model results are still pending for ${ceTotal} location-evaluation(s) — ` +
          'use "Re-run authority checks" to start the model run; the designation refreshes when it lands.'
        : `Why: ${ceTotal} location-evaluation(s) could not be completed (${ceGates.map((g) => gateName(g.key)).join(', ')}) — ` +
          'authority is not granted over un-assessed data. Resolve the flagged locations to clear the referral.'
      : null;

  return (
    <div style={S.panel}>
      <div style={S.head}>
        <h3 style={S.title}>Underwriting Authority</h3>
        <span style={S.meta}>
          {evaluatedAt ? `Evaluated ${evaluatedAt}` : ''}
          {data.configVersion ? `${evaluatedAt ? ' · ' : ''}config ${data.configVersion}` : ''}
        </span>
        {/* UND-715: evaluation no longer re-runs on tab reads — this is the explicit trigger. */}
        <Button
          variant="text"
          color="$primary"
          label={rerunning ? 'Re-running…' : '⟳ Re-run authority checks'}
          onPress={rerun}
          disabled={rerunning || macavity.active}
        />
      </div>

      <div style={{ ...S.designation, background: d.bg, borderColor: d.border }}>
        <span style={{ ...S.badge, background: d.badge }}>{d.label}</span>
        <div>
          <div style={S.dMsg}>{d.msg}</div>
          {ceNote && <div style={S.dWhy}>{ceNote}</div>}
        </div>
      </div>

      {macavity.active && (
        <div style={S.recompute}>
          <ActivityIndicator color="$primary" />
          <div style={S.recomputeBody}>
            <div style={S.recomputeText}>
              Macavity is recomputing catastrophe terms — the loss-pick and hex-cell PML checks will refresh automatically.
            </div>
            <div style={S.recomputeTrack}>
              <div style={{ ...S.recomputeFill, width: `${macavity.progress}%` }} />
            </div>
          </div>
          <span style={S.recomputePct}>{macavity.progress}%</span>
        </div>
      )}

      <div style={S.gates}>
        {rows.map((g, i) => {
          const findings = g.findings || [];
          // A gate can PASS its breach check yet flag locations it could not evaluate (missing
          // zone/deductible/TIV/KatRisk). Those force the overall referral, so surface them on the
          // row even when the pill is green — otherwise the referral looks unexplained.
          const cannotEval = findings.filter((f) => f.cannotEvaluate).length;
          // While a macavity recompute is in flight, a macavity-gated check that currently can't
          // evaluate is transient — show it as "Pending" (frontend-only), not a referral.
          const recomputing = (macavity.active || healing) && MACAVITY_GATES.has(g.key) && g.outcome === 'ERROR';
          const o = recomputing ? OUTCOME.PENDING : OUTCOME[g.outcome] || OUTCOME.NOT_APPLICABLE;
          const compositionRows = (g.detail && g.detail.compositionRows) || [];
          // The advisory-only GWP Floor diagnostic isn't a standalone gate underwriters act on —
          // tuck it inside the GWP Bounds (Gate 1) row.
          const nestedFloor = g.key === 'gwp_bounds' ? gwpFloor : null;
          // Gate 2 carries the Technical Premium build-up in its detail — show it as a stacked bar.
          const tpDetail = g.key === 'technical_premium' && g.detail && g.detail.technicalPremium != null ? g.detail : null;
          // Gate 2b carries the loss-pick computation in its detail — show the breakdown.
          const lossPickDetail = g.key === 'loss_pick_max' && g.detail && g.detail.lossPickPct != null ? g.detail : null;
          // Per-location checks: every gate that examines locations emits one row per location — on
          // pass and on fail — so an underwriter can always see why, not just a green check.
          const locationChecks = (g.detail && g.detail.locationChecks) || [];
          const diagnostic = DIAGNOSTIC_TAGGED.has(g.key);
          const hasStructured =
            compositionRows.length > 0 || !!nestedFloor || !!tpDetail || !!lossPickDetail || locationChecks.length > 0 || findings.length > 0;
          // Every gate is openable: even a clean pass should explain itself (criteria + reasoning).
          const expandable = true;
          return (
            <details
              key={g.key || i}
              style={S.gate}
              onToggle={
                expandable
                  ? (e) => {
                      // Capture `open` synchronously — React nulls currentTarget before the
                      // deferred setState updater runs.
                      const isOpen = e.currentTarget.open;
                      setOpenGates((s) => ({ ...s, [g.key || i]: isOpen }));
                      if (g.key === 'tiv_aggregation' && isOpen && cells == null) {
                        getQuoteAuthorityCells(quoteId)
                          .then(setCells)
                          .catch(() => setCells({ error: true }));
                      }
                    }
                  : undefined
              }
            >
              <summary style={{ ...S.summary, cursor: expandable ? 'pointer' : 'default' }}>
                <span
                  aria-hidden
                  style={{
                    ...S.chev,
                    // Reserve the column on every row so gate numbers stay aligned; only show
                    // the chevron where there's something to expand.
                    visibility: expandable ? 'visible' : 'hidden',
                    transform: openGates[g.key || i] ? 'rotate(90deg)' : 'none',
                  }}
                >
                  ›
                </span>
                <span style={S.gnum}>{GATE_NUMBER[g.key] || i + 1}</span>
                <span style={S.gname}>
                  <span style={S.gnameMain}>
                    {gateName(g.key)}
                    {diagnostic && <span style={S.tag}>Diagnostic</span>}
                  </span>
                  {GATE_CRITERIA[g.key] && <span style={S.gcriteria}>{GATE_CRITERIA[g.key]}</span>}
                </span>
                <span style={{ ...S.pill, color: o.color, background: o.bg }}>{o.label}</span>
                {recomputing ? (
                  <span style={S.recomputeChip} title="Macavity is recomputing this gate's inputs; it will resolve automatically.">
                    ⟳ recomputing…
                  </span>
                ) : (
                  cannotEval > 0 && (
                    <span
                      style={S.cebadge}
                      title="These locations could not be evaluated; authority is not granted over un-assessed data, so the quote is referred."
                    >
                      ⚠ {cannotEval} can’t evaluate
                    </span>
                  )
                )}
                <span style={S.gsum}>
                  <span style={{ ...S.ic, color: o.iconColor }}>{o.icon}</span>
                  {withCommas(g.summary)}
                </span>
              </summary>
              {expandable && (
                <div style={S.gdetail}>
                  {tpDetail && <TechnicalPremiumChart detail={tpDetail} />}
                  {lossPickDetail && <LossPickBreakdown detail={lossPickDetail} />}
                  {compositionRows.length > 0 && (
                    <CompositionChart rows={compositionRows} locName={locName} rings={g.key === 'tiv_ring_aggregation'} />
                  )}
                  {g.key === 'tiv_ring_aggregation' && portfolioGapNote(g.detail) && <div style={S.whyLine}>{portfolioGapNote(g.detail)}</div>}
                  {g.key === 'tiv_aggregation' && cells && !cells.error && <CellMap data={cells} />}
                  {nestedFloor && <FloorDiagnostic gate={nestedFloor} />}
                  {/* Per-location checks supersede the flat findings table — they already include the
                      failing rows, with the cleared ones alongside. Render findings only when there
                      are no checks. The hex-cell PML check lists per-location detail inside its
                      cluster sub-sections, so suppress the flat table there. */}
                  {locationChecks.length > 0 && <LocationChecksTable rows={locationChecks} locName={locName} />}
                  {findings.length > 0 && locationChecks.length === 0 && g.key !== 'tiv_aggregation' && (
                    <table style={S.findings}>
                      <thead>
                        <tr>
                          <th style={S.th}>Location</th>
                          <th style={S.th}>Finding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {findings.map((f, j) => (
                          // A location can carry several findings (the TIV rings check gives one per
                          // breached radius), so the row's position is part of its key.
                          <tr key={`${j}-${f.locationId}`}>
                            <td style={S.td}>{locName(f.locationId)}</td>
                            <td style={S.td}>{findingText(f)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {/* No structured detail (a quote-level gate with only a summary) — still explain the
                      result so opening the row is never a dead end. */}
                  {!hasStructured && <div style={S.whyLine}>{withCommas(g.summary) || 'No issues found.'}</div>}
                </div>
              )}
            </details>
          );
        })}
      </div>

      <div style={S.foot}>
        <span>{data.configVersion ? ` Thresholds config version ${data.configVersion}` : ''}</span>
        <span>·</span>
        <span>Re-evaluates automatically on every adjustment</span>
      </div>
    </div>
  );
};

const S = {
  panel: { background: '#fff', borderRadius: '0 0 8px 8px', padding: 24 },
  muted: { padding: 24, color: C.ink500, fontSize: 14 },
  loading: { display: 'flex', alignItems: 'center', gap: 14, padding: 24 },
  loadingTitle: { color: C.ink900, fontSize: 14, fontWeight: 600 },
  loadingSub: { marginTop: 2, color: C.ink500, fontSize: 12, lineHeight: '16px' },
  // In-flight macavity recompute banner: spinner + label + a thin heuristic progress bar.
  recompute: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: '#eef3fb',
    border: '1px solid #cfdcef',
    borderRadius: 8,
  },
  recomputeBody: { flex: 1, minWidth: 0 },
  recomputeText: { color: C.ink700, fontSize: 13, lineHeight: '18px' },
  recomputeTrack: { marginTop: 8, height: 6, background: '#dbe5f4', borderRadius: 9999, overflow: 'hidden' },
  recomputeFill: { height: '100%', background: C.navy, borderRadius: 9999, transition: 'width 0.4s ease' },
  recomputePct: { flex: 'none', color: C.navy, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  recomputeChip: {
    flex: 'none',
    fontSize: 11,
    fontWeight: 700,
    color: C.navy,
    background: '#eef3fb',
    border: '1px solid #cfdcef',
    borderRadius: 9999,
    padding: '3px 9px',
    whiteSpace: 'nowrap',
  },
  wip: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    margin: '0 0 16px',
    padding: '10px 14px',
    background: C.amberBg,
    border: `1px solid ${C.amberBorder}`,
    borderRadius: 8,
    color: C.amberText,
    fontSize: 13,
    lineHeight: '18px',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: 16, lineHeight: '28px', fontWeight: 600, color: C.ink900 },
  meta: { marginLeft: 'auto', fontSize: 12, lineHeight: '16px', color: C.ink300 },
  designation: { marginTop: 16, display: 'flex', gap: 12, alignItems: 'flex-start', border: '1px solid', borderRadius: 8, padding: '14px 16px' },
  badge: {
    flex: 'none',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    color: '#fff',
    borderRadius: 4,
    padding: '6px 10px',
    marginTop: 1,
  },
  dMsg: { color: C.ink900, fontWeight: 500 },
  dWhy: { marginTop: 6, color: C.amberText, fontSize: 13, lineHeight: '18px' },
  cebadge: {
    flex: 'none',
    fontSize: 11,
    fontWeight: 700,
    color: C.amberText,
    background: C.amberBg,
    border: `1px solid ${C.amberBorder}`,
    borderRadius: 9999,
    padding: '3px 9px',
    whiteSpace: 'nowrap',
  },
  gates: { marginTop: 20, borderTop: `1px solid ${C.border}` },
  gate: { borderBottom: `1px solid ${C.border}` },
  summary: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 4px', listStyle: 'none' },
  chev: {
    width: 10,
    flex: 'none',
    color: C.ink300,
    fontSize: 15,
    lineHeight: 1,
    display: 'inline-block',
    textAlign: 'center',
    transition: 'transform .15s ease',
  },
  gnum: { color: C.ink300, fontSize: 12, width: 22, flex: 'none' },
  gname: { width: 230, flex: 'none', display: 'flex', flexDirection: 'column', gap: 2 },
  gnameMain: { fontWeight: 600, color: C.ink900, fontSize: 14 },
  gcriteria: { fontSize: 11, lineHeight: '14px', color: C.ink500, fontWeight: 400 },
  pill: { fontSize: 11, fontWeight: 700, letterSpacing: '.04em', borderRadius: 9999, padding: '3px 10px', flex: 'none' },
  gsum: { color: C.ink500, fontSize: 13 },
  ic: { fontWeight: 700, marginRight: 6 },
  tag: {
    display: 'inline-block',
    marginLeft: 8,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: C.ink500,
    background: '#eef1f3',
    borderRadius: 4,
    padding: '2px 6px',
    verticalAlign: 'middle',
  },
  gdetail: { padding: '12px 8px 18px 36px', background: C.headerBg },
  whyLine: { fontSize: 13, color: C.ink700, lineHeight: '18px' },
  floorRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 8px', fontSize: 13, color: C.ink500 },
  floorLabel: { fontWeight: 600, color: C.ink700, flex: 'none' },
  floorSum: { color: C.ink500 },
  findings: { borderCollapse: 'collapse', width: '100%', maxWidth: 700, fontSize: 13 },
  // Per-location checks table: no 700px cap, so on wide screens the Location/Check columns get the
  // full panel width and stop wrapping prematurely. The Location column never wraps (dealer names
  // stay on one line); the Check column takes the remaining space.
  checksScroll: { overflowX: 'auto' },
  checksTable: { borderCollapse: 'collapse', width: '100%', minWidth: 560, fontSize: 13 },
  ckLoc: { padding: '7px 24px 7px 0', borderBottom: '1px solid #eceff1', color: C.ink700, whiteSpace: 'nowrap' },
  ckText: { padding: '7px 0', borderBottom: '1px solid #eceff1', color: C.ink700, minWidth: 320 },
  th: {
    textAlign: 'left',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: C.ink300,
    fontWeight: 600,
    padding: '5px 12px 5px 0',
    borderBottom: `1px solid ${C.border}`,
  },
  td: { padding: '7px 12px 7px 0', borderBottom: '1px solid #eceff1', color: C.ink700 },
  foot: { display: 'flex', gap: 16, alignItems: 'center', marginTop: 18, fontSize: 12, color: C.ink300, flexWrap: 'wrap' },
  aggBlock: { maxWidth: 780, marginBottom: 8 },
  aggDetails: { maxWidth: 780, borderTop: `1px solid ${C.border}` },
  aggSummary: { cursor: 'pointer', listStyle: 'none', fontSize: 12, fontWeight: 600, color: C.ink900, padding: '8px 0' },
  h4: { margin: '0 0 10px', fontSize: 12, lineHeight: '16px', textTransform: 'uppercase', letterSpacing: '.05em', color: C.ink500, fontWeight: 600 },
  aggRow: { display: 'grid', gridTemplateColumns: '48px 1fr 200px', gap: 12, alignItems: 'center', margin: '10px 0' },
  aggRadius: { fontSize: 12, color: C.ink500, textAlign: 'right' },
  barwrap: { position: 'relative', height: 22, background: '#ECEFF1', borderRadius: 4 },
  bar: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', borderRadius: 4, overflow: 'hidden' },
  seg: { height: '100%' },
  limitMark: { position: 'absolute', top: -4, bottom: -4, width: 2, background: C.ink900 },
  aggTotal: { fontSize: 12 },
  legend: { display: 'flex', gap: 18, margin: '14px 0 0', fontSize: 12, color: C.ink500, flexWrap: 'wrap' },
  legSwatch: { display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 6, verticalAlign: -1 },
  legLimit: { display: 'inline-block', width: 2, height: 12, background: C.ink900, marginRight: 8, verticalAlign: -2 },
  // Technical Premium build-up (Gate 2): one stacked bar + floor marker + GWP diamond.
  tpWrap: { maxWidth: 780 },
  tpBarWrap: { position: 'relative', height: 26, marginTop: 4, marginBottom: 14 },
  tpBar: { position: 'absolute', top: 4, left: 0, right: 0, height: 18, display: 'flex', borderRadius: 4, overflow: 'hidden', background: '#ECEFF1' },
  tpFloorMark: { position: 'absolute', top: 0, height: 26, width: 2, background: '#e8765b' },
  tpGwpMark: { position: 'absolute', top: -2, fontSize: 14, lineHeight: '14px', color: C.ink900 },
  tpVerdict: { marginTop: 10, fontSize: 13, fontWeight: 600 },
  legFloor: { display: 'inline-block', width: 12, height: 3, background: '#e8765b', marginRight: 8, verticalAlign: 3 },
  tdNum: { padding: '7px 0', borderBottom: '1px solid #eceff1', color: C.ink700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  tdB: { padding: '7px 12px 7px 0', borderBottom: '1px solid #eceff1', color: C.ink900, fontWeight: 700 },
  tdNumB: {
    padding: '7px 0',
    borderBottom: '1px solid #eceff1',
    color: C.ink900,
    fontWeight: 700,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
};
